/**
 * Gestione dei Banner della homepage.
 *
 * Riscritta (non estesa) rispetto alla versione mock: quella gestiva la
 * Striscia promo — `{ text, code, link, endDate }`, testo e codice sconto,
 * nessuna immagine — che è una cosa diversa dai Banner e tornerà per conto
 * suo come `slot: "topbar"`. Il modello dati non aveva nulla in comune.
 *
 * Due sezioni, una per Slot. Le anteprime sono affiancate perché è il
 * confronto desktop/mobile che conta: è lì che si vede se l'asset mobile
 * manca o se il ritaglio mangia il contenuto.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  GripVertical, Image as ImageIcon, Loader2, Monitor, Plus, Smartphone,
  Trash2, Upload, AlertTriangle, Clock, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Popover as PopoverRoot,
  PopoverTrigger as PopoverTriggerRoot,
  PopoverContent as PopoverContentRoot,
} from '../../components/ui/popover';
import {
  bannerService, bannerImageUrl, checkAsset, isExpired, assetTargetFor,
  HERO_MAX_BANNERS,
  type Banner, type BannerSlot, type BannerVariant,
} from '../../services/bannerService';

// ── Configurazione per Slot ───────────────────────────────────────────────
// Il massimo è validato qui e non in Mongo: è un limite redazionale, non
// un vincolo di integrità (spec.md §4.4).
interface SlotConfig {
  label: string;
  description: string;
  max: number;
  /** Solo `hero`: la sidebar è nascosta sotto i 640px, un asset mobile lì
   *  non verrebbe mai mostrato. */
  hasMobile: boolean;
}

const SLOT_CONFIG: Record<BannerSlot, SlotConfig> = {
  hero: {
    label: 'Hero',
    description:
      'Carosello accanto a "Ricerca avanzata". Ruota ogni 5 secondi se ci sono più banner attivi, resta fermo se ce n\'è uno solo.',
    max: HERO_MAX_BANNERS,
    hasMobile: true,
  },
  sidebar: {
    label: 'Sidebar',
    description:
      'Colonna a destra della vetrina prodotti. Non compare sotto i 640px di larghezza, quindi non serve un asset mobile.',
    max: 4,
    hasMobile: false,
  },
};

// I primitivi Popover sono .jsx senza tipi: si ritipizzano qui, come fa
// già CatEscluseBadge in abbinatore-v2.
const Popover = PopoverRoot as React.FC<{ open?: boolean; onOpenChange?: (o: boolean) => void; children?: React.ReactNode }>;
const PopoverTrigger = PopoverTriggerRoot as React.FC<{ asChild?: boolean; children?: React.ReactNode }>;
const PopoverContent = PopoverContentRoot as React.FC<{ className?: string; align?: 'start' | 'center' | 'end'; sideOffset?: number; children?: React.ReactNode }>;

const SLOTS: BannerSlot[] = ['hero', 'sidebar'];

// ── Badge ─────────────────────────────────────────────────────────────────

function StatusBadge({ active }: { active: boolean }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded font-bold ${
      active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
    }`}>
      {active ? 'ATTIVO' : 'DISATTIVATO'}
    </span>
  );
}

/**
 * Badge "scaduto".
 *
 * È l'unica mitigazione all'assenza di uno scheduler: `endsAt` non viene
 * filtrato server-side, quindi un banner scaduto resta pubblicato finché
 * qualcuno non lo disattiva a mano. Per questo deve essere visibile.
 */
function ExpiredBadge({ endsAt }: { endsAt: string }) {
  return (
    <span
      className="text-xs px-2 py-0.5 rounded font-bold bg-amber-100 text-amber-800 inline-flex items-center gap-1"
      title={`La data di fine (${endsAt}) è passata, ma il banner è ancora pubblicato: disattivalo per rimuoverlo dalla homepage.`}
    >
      <Clock className="w-3 h-3" /> SCADUTO
    </span>
  );
}

// ── Anteprima di una variante ─────────────────────────────────────────────

interface PreviewProps {
  banner: Banner;
  variant: BannerVariant;
  uploading: boolean;
  onUpload: (file: File) => void;
  onRemove: () => void;
}

function VariantPreview({ banner, variant, uploading, onUpload, onRemove }: PreviewProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const image = variant === 'desktop' ? banner.desktopImage : banner.mobileImage;
  const url = bannerImageUrl(image);
  const target = assetTargetFor(banner.slot, variant);
  const Icon = variant === 'desktop' ? Monitor : Smartphone;

  // Il box dell'anteprima usa il rapporto di destinazione, non quello
  // dell'asset: così un asset fuori formato si vede ritagliato esattamente
  // come apparirà in homepage.
  //
  // L'immagine è il contenuto del banner, quindi ha la parte maggiore della
  // card: è guardandola che si decide se va bene. Le larghezze sono fissate
  // per rapporto (non `flex-1`, che su un 4:5 darebbe un box alto il doppio
  // della card) e le due varianti dell'hero risultano della stessa altezza,
  // 225px, così la riga non è a scaletta.
  const { aspect, width } = variant === 'mobile'
    ? { aspect: 'aspect-square', width: 'w-[225px]' }
    : banner.slot === 'sidebar'
      ? { aspect: 'aspect-[4/5]', width: 'w-[260px]' }
      : { aspect: 'aspect-[16/9]', width: 'w-[400px]' };

  return (
    <div className={`${width} flex-shrink-0`}>
      {/* Nome della variante e formato atteso su due righe: su una riga
          sola il "1080×1080" sbordava dalla colonna mobile. */}
      <div className="mb-1.5">
        <div className="flex items-center justify-between gap-1">
          <span className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
            <Icon className="w-3.5 h-3.5" />
            {variant === 'desktop' ? 'Desktop' : 'Mobile'}
          </span>
          {url && (
            <button
              onClick={onRemove}
              className="text-xs text-muted-foreground hover:text-red-500 underline"
              data-testid={`banner-${banner.id}-remove-${variant}`}
            >
              rimuovi
            </button>
          )}
        </div>
        {target && (
          <span className="block text-xs text-muted-foreground/60 leading-tight">
            {target.width}×{target.height} · {target.ratioLabel}
          </span>
        )}
      </div>

      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className={`relative w-full ${aspect} rounded-lg overflow-hidden border-2 border-dashed transition-colors group ${
          url ? 'border-transparent bg-secondary/30' : 'border-gray-300 bg-secondary/20 hover:border-[hsl(214,58%,41%)]'
        }`}
        data-testid={`banner-${banner.id}-upload-${variant}`}
      >
        {url ? (
          <img src={url} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-muted-foreground">
            <Upload className="w-4 h-4" />
            <span className="text-xs">carica</span>
          </span>
        )}
        {uploading && (
          <span className="absolute inset-0 flex items-center justify-center bg-white/70">
            <Loader2 className="w-4 h-4 animate-spin" />
          </span>
        )}
        {url && (
          <span className="absolute inset-0 hidden group-hover:flex items-center justify-center bg-black/40 text-white text-xs font-medium">
            sostituisci
          </span>
        )}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) onUpload(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}

/**
 * Avvisi del banner, ridotti a un'icona con popover.
 *
 * Gli avvisi sono discorsivi per necessità — dicono la conseguenza, non
 * solo che qualcosa manca — e per esteso occupavano piu' spazio della
 * card che commentano. Qui restano leggibili ma costano un'icona: chi ha
 * bisogno di sapere apre, chi sta solo scorrendo la lista vede che c'è
 * qualcosa da leggere.
 *
 * Nessuno è bloccante: gli asset sono già salvati e un banner fuori
 * specifica pubblicato consapevolmente è meglio di uno slot vuoto.
 * Servono a far sapere all'admin che non è conforme *prima* che finisca
 * in homepage, non a impedirglielo.
 */
interface BannerWarning {
  /** Intestazione dentro il popover, es. "Asset desktop". */
  scope: string;
  body: React.ReactNode;
}

function WarningsPopover({ bannerId, warnings }: { bannerId: string; warnings: BannerWarning[] }) {
  const [open, setOpen] = useState(false);
  if (warnings.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center gap-1.5 text-xs font-medium bg-amber-50 border border-amber-200 text-amber-800 px-2 h-9 rounded-lg hover:bg-amber-100 transition-colors"
          data-testid={`banner-${bannerId}-warnings`}
          title={`${warnings.length} avviso/i su questo banner`}
        >
          <AlertTriangle className="w-3.5 h-3.5" />
          {warnings.length}
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-3 w-80" align="start" sideOffset={4}>
        <div className="text-xs font-bold text-muted-foreground mb-2 uppercase tracking-wider">
          {warnings.length === 1 ? 'Avviso' : `${warnings.length} avvisi`}
        </div>
        <div className="space-y-2.5">
          {warnings.map((w, i) => (
            <div key={i} className="text-[13px] leading-snug">
              <div className="font-medium text-foreground">{w.scope}</div>
              <div className="text-muted-foreground">{w.body}</div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── Conferma delle azioni distruttive ─────────────────────────────────────

interface ConfirmRequest {
  title: string;
  /** Cosa succede se si conferma. Non "sei sicuro?": la conseguenza. */
  body: React.ReactNode;
  confirmLabel: string;
  onConfirm: () => void | Promise<void>;
}

/**
 * Modale di conferma per rimozione immagine ed eliminazione banner.
 *
 * Sostituisce `window.confirm`, che il browser rende come un alert di
 * sistema: nessuna formattazione, quindi la conseguenza dell'azione
 * arrivava come un muro di testo, ed è il tipo di dialog che si chiude di
 * riflesso senza leggerlo. Qui la conseguenza è in evidenza e il pulsante
 * di conferma dice cosa fa, non "OK".
 *
 * Stile della casa (`fixed inset-0 z-[80]` + card bianca, come
 * AdminNewsPage), più le parti che il pattern a mano non ha: `role`/
 * `aria-modal`, chiusura con Escape e focus iniziale sul pulsante che
 * annulla — su un'azione irreversibile il default da tastiera non deve
 * essere quello che distrugge.
 */
function ConfirmDialog({ request, onClose }: { request: ConfirmRequest | null; onClose: () => void }) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!request) return;
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [request, onClose]);

  if (!request) return null;

  const run = async () => {
    setWorking(true);
    try {
      await request.onConfirm();
      onClose();
    } finally {
      setWorking(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="banner-confirm-overlay"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="banner-confirm-title"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
        onClick={e => e.stopPropagation()}
        data-testid="banner-confirm-dialog"
      >
        <h3 id="banner-confirm-title" className="text-lg font-semibold tracking-tight mb-2">
          {request.title}
        </h3>
        <div className="text-sm text-muted-foreground leading-relaxed">{request.body}</div>
        <div className="flex justify-end gap-2 mt-5">
          <button
            ref={cancelRef}
            onClick={onClose}
            className="h-9 px-4 rounded-lg border text-sm font-medium hover:bg-secondary/50"
            data-testid="banner-confirm-cancel"
          >
            Annulla
          </button>
          <button
            onClick={run}
            disabled={working}
            className="h-9 px-4 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors flex items-center gap-1.5 disabled:opacity-60"
            data-testid="banner-confirm-ok"
          >
            {working && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {request.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Card di un banner ─────────────────────────────────────────────────────

interface CardProps {
  banner: Banner;
  config: SlotConfig;
  onChanged: (updated: Banner) => void;
  onDeleted: () => void;
  dragHandlers: {
    onDragStart: () => void;
    onDragEnter: () => void;
    onDragEnd: () => void;
  };
}

function BannerCard({ banner, config, onChanged, onDeleted, dragHandlers }: CardProps) {
  // I campi di testo si raccolgono in una bozza e si salvano con un
  // pulsante: digitare non deve produrre una richiesta per carattere.
  // I controlli discreti (attivo, pubblico) salvano subito, perché un
  // interruttore che non fa nulla finché non premi "Salva" è peggio.
  const [draft, setDraft] = useState({
    alt: banner.alt,
    href: banner.href ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<BannerVariant | null>(null);
  const [warnings, setWarnings] = useState<Record<string, string[]>>({});
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null);

  useEffect(() => {
    setDraft({ alt: banner.alt, href: banner.href ?? '' });
  }, [banner.alt, banner.href]);

  const dirty =
    draft.alt !== banner.alt ||
    draft.href !== (banner.href ?? '');

  const saveDraft = async () => {
    if (!draft.alt.trim()) {
      toast.error('Il testo alternativo è obbligatorio.');
      return;
    }
    setSaving(true);
    try {
      // `endsAt` non è nel payload di proposito: il campo non è più
      // esposto (vedi sotto), e mandarlo azzererebbe il valore di un
      // banner che ce l'ha già in Mongo a ogni salvataggio.
      const updated = await bannerService.update(banner.id, {
        alt: draft.alt.trim(),
        href: draft.href.trim() || null,
      });
      onChanged(updated);
      toast.success('Banner aggiornato');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? 'Errore salvataggio banner');
    } finally {
      setSaving(false);
    }
  };

  const patch = async (payload: Parameters<typeof bannerService.update>[1]) => {
    try {
      onChanged(await bannerService.update(banner.id, payload));
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? 'Errore salvataggio banner');
    }
  };

  const upload = async (variant: BannerVariant, file: File) => {
    setUploading(variant);
    try {
      const res = await bannerService.uploadImage(banner.id, variant, file);
      onChanged(res.banner);
      const check = checkAsset(banner.slot, variant, res.image);
      setWarnings(w => ({ ...w, [variant]: check.warnings }));
      if (check.warnings.length > 0) {
        toast.warning(`Immagine caricata, ma non rispetta la specifica (${check.warnings.length} avvis${check.warnings.length === 1 ? 'o' : 'i'})`);
      } else {
        toast.success('Immagine caricata');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? 'Errore caricamento immagine');
    } finally {
      setUploading(null);
    }
  };

  /**
   * Rimozione immagine: sempre dietro conferma.
   *
   * Il click su "rimuovi" è a pochi pixel dall'anteprima e l'azione non è
   * annullabile dall'interfaccia — il file resta su disco, ma per
   * rimetterlo al suo posto va ricaricato a mano.
   */
  const askRemoveImage = (variant: BannerVariant) => {
    setConfirm({
      title: variant === 'desktop' ? 'Rimuovere l\'immagine desktop?' : 'Rimuovere l\'immagine mobile?',
      body: (
        <>
          <p className="mb-2">
            Banner <strong className="text-foreground">{banner.alt}</strong>.
          </p>
          <p className="mb-2">
            {variant === 'desktop'
              ? 'Senza immagine desktop il banner smette di essere pubblicato in homepage.'
              : 'Senza immagine mobile, sotto i 640px verrà usata quella desktop, ritagliata a quadrato, perdendo il 44% della larghezza.'}
          </p>
          <p>Per ripristinarla andrà ricaricata.</p>
        </>
      ),
      confirmLabel: 'Rimuovi immagine',
      onConfirm: async () => {
        try {
          await bannerService.removeImage(banner.id, variant);
          setWarnings(w => ({ ...w, [variant]: [] }));
          onChanged({
            ...banner,
            [variant === 'desktop' ? 'desktopImage' : 'mobileImage']: null,
          });
          toast.success('Immagine rimossa');
        } catch {
          toast.error('Errore rimozione immagine');
        }
      },
    });
  };

  const askRemove = () => {
    setConfirm({
      title: 'Eliminare il banner?',
      body: (
        <>
          <p className="mb-2">
            Banner <strong className="text-foreground">{banner.alt}</strong>.
          </p>
          <p>
            Il banner e le sue impostazioni vengono eliminati e non è possibile
            annullare l'operazione. Per togliere un banner dalla homepage
            lasciandolo configurato, togli invece la spunta ad "Attivo".
          </p>
        </>
      ),
      confirmLabel: 'Elimina banner',
      onConfirm: async () => {
        try {
          await bannerService.remove(banner.id);
          onDeleted();
          toast.success('Banner eliminato');
        } catch {
          toast.error('Errore eliminazione banner');
        }
      },
    });
  };

  const noDesktop = !banner.desktopImage;
  const noMobile = config.hasMobile && !banner.mobileImage;

  // Gli avvisi di tutte le provenienze in una lista sola: l'asset mobile
  // che manca, più quelli di conformità prodotti dall'ultimo upload.
  const bannerWarnings: BannerWarning[] = [
    ...(noMobile ? [{
      scope: 'Nessun asset mobile',
      body: (
        <>
          Sotto i 640px verrà usato quello desktop, ritagliato a quadrato,{' '}
          <strong className="font-medium text-amber-700">perdendo il 44% della larghezza</strong>.
          Un claim centrato sopravvive, un layout con logo a sinistra e
          prodotto a destra no.
        </>
      ),
    }] : []),
    ...(warnings.desktop ?? []).map(w => ({ scope: 'Asset desktop', body: w })),
    ...(warnings.mobile ?? []).map(w => ({ scope: 'Asset mobile', body: w })),
  ];

  return (
    <div
      draggable
      onDragStart={dragHandlers.onDragStart}
      onDragEnter={dragHandlers.onDragEnter}
      onDragEnd={dragHandlers.onDragEnd}
      onDragOver={e => e.preventDefault()}
      className={`rounded-xl border bg-white p-4 ${banner.active ? '' : 'bg-secondary/20'}`}
      data-testid={`banner-${banner.id}`}
    >
      <div className="flex flex-wrap gap-4">
        {/* Maniglia di trascinamento */}
        <div className="flex flex-col items-center pt-1 cursor-grab active:cursor-grabbing flex-shrink-0">
          <GripVertical className="w-4 h-4 text-muted-foreground/50" />
          <span className="text-xs font-mono text-muted-foreground mt-1">{banner.position + 1}</span>
        </div>

        {/* Anteprime */}
        <div className="flex gap-3 flex-shrink-0">
          <VariantPreview
            banner={banner}
            variant="desktop"
            uploading={uploading === 'desktop'}
            onUpload={f => upload('desktop', f)}
            onRemove={() => askRemoveImage('desktop')}
          />
          {config.hasMobile && (
            <VariantPreview
              banner={banner}
              variant="mobile"
              uploading={uploading === 'mobile'}
              onUpload={f => upload('mobile', f)}
              onRemove={() => askRemoveImage('mobile')}
            />
          )}
        </div>

        {/* Campi */}
        <div className="flex-1 min-w-[320px] space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge active={banner.active} />
            {banner.endsAt && isExpired(banner.endsAt) && <ExpiredBadge endsAt={banner.endsAt} />}
            {noDesktop && (
              <span className="text-xs px-2 py-0.5 rounded font-bold bg-gray-100 text-gray-500">
                BOZZA · senza immagine desktop non viene pubblicato
              </span>
            )}
          </div>

          {/* I campi di testo sono corti: nessuno dei due contiene valori
              lunghi, e un input largo mezza pagina suggerisce il contrario. */}
          <div>
            <label className="text-sm font-medium block mb-1">
              Testo alternativo <span className="text-red-500">*</span>
              <span className="block text-xs font-normal text-muted-foreground mt-0.5">
                Per l'accessibilità: è ciò che leggono i lettori di schermo, ed
                è l'unico testo del banner.
              </span>
            </label>
            <input
              value={draft.alt}
              onChange={e => setDraft(d => ({ ...d, alt: e.target.value }))}
              className="w-full max-w-[380px] h-9 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)]/40"
              placeholder="Promo Stampanti — fino al -30%"
              data-testid={`banner-${banner.id}-alt`}
            />
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">
              Destinazione
              <span className="text-xs font-normal text-muted-foreground ml-1.5">vuoto = non cliccabile</span>
            </label>
            <input
              value={draft.href}
              onChange={e => setDraft(d => ({ ...d, href: e.target.value }))}
              className="w-full max-w-[240px] h-9 px-3 rounded-lg border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[hsl(214,58%,41%)]/40"
              placeholder="/prodotti/tutti"
              data-testid={`banner-${banner.id}-href`}
            />
          </div>

          {/* "Fine promo" (`endsAt`) non è esposta: era un campo informativo
              senza scheduler dietro, quindi non cambiava cosa viene
              pubblicato. Il dato resta nel modello e il badge "scaduto" qui
              sopra continua a mostrarlo se un banner ce l'ha; per
              rimetterlo servono questo input e il campo nel payload di
              `saveDraft`. */}

          {/* Radio invece di una tendina: le opzioni sono tre e si escludono,
              quindi si leggono tutte insieme senza aprire nulla.
              In colonna e non in riga: con le anteprime piu' grandi la
              colonna dei campi non tiene tre etichette affiancate e le
              mandava a capo 2+1, che si legge peggio di tre righe pari. */}
          <fieldset>
            <legend className="text-sm font-medium mb-1.5">Visibile a</legend>
            <div className="flex flex-col gap-1.5" data-testid={`banner-${banner.id}-audience`}>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  type="radio"
                  name={`audience-${banner.id}`}
                  checked={banner.audience === 'tutti'}
                  onChange={() => patch({ audience: 'tutti' })}
                  data-testid={`banner-${banner.id}-audience-tutti`}
                />
                Tutti i visitatori
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  type="radio"
                  name={`audience-${banner.id}`}
                  checked={banner.audience === 'anonimi'}
                  onChange={() => patch({ audience: 'anonimi' })}
                  data-testid={`banner-${banner.id}-audience-anonimi`}
                />
                Solo non registrati
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  type="radio"
                  name={`audience-${banner.id}`}
                  checked={banner.audience === 'autenticati'}
                  onChange={() => patch({ audience: 'autenticati' })}
                  data-testid={`banner-${banner.id}-audience-autenticati`}
                />
                Solo utenti registrati
              </label>
            </div>
          </fieldset>

          <div className="flex items-end gap-2 flex-wrap">
            <label className="h-9 px-3 rounded-lg border flex items-center gap-2 text-sm font-medium cursor-pointer hover:bg-secondary/50 select-none">
              <input
                type="checkbox"
                checked={banner.active}
                onChange={e => patch({ active: e.target.checked })}
                className="rounded"
                data-testid={`banner-${banner.id}-active`}
              />
              Attivo
            </label>

            {dirty && (
              <button
                onClick={saveDraft}
                disabled={saving}
                className="h-9 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium hover:bg-[hsl(214,58%,35%)] transition-colors flex items-center gap-1.5 disabled:opacity-60"
                data-testid={`banner-${banner.id}-save`}
              >
                {saving && <Loader2 className="w-3 h-3 animate-spin" />}
                Salva modifiche
              </button>
            )}

            {banner.href && (
              <a
                href={banner.href}
                target="_blank"
                rel="noreferrer"
                className="h-9 px-2.5 rounded-lg border flex items-center text-muted-foreground hover:text-foreground"
                title="Apri la destinazione in una nuova scheda"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}

            <button
              onClick={askRemove}
              className="h-9 w-9 rounded-lg border flex items-center justify-center text-muted-foreground hover:text-red-500 hover:bg-red-50 hover:border-red-200 transition-colors"
              title="Elimina banner"
              data-testid={`banner-${banner.id}-delete`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>

            {/* Tutti gli avvisi del banner in un'unica icona, in fila con
                gli altri comandi. Ogni avviso dice cosa succede, non solo
                che manca qualcosa: uno senza conseguenza non aiuta a
                decidere, ed è per questo che sono lunghi e stanno in un
                popover invece che nella card. */}
            <WarningsPopover bannerId={banner.id} warnings={bannerWarnings} />
          </div>
        </div>
      </div>

      <ConfirmDialog request={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

// ── Sezione di uno Slot ───────────────────────────────────────────────────

interface SectionProps {
  slot: BannerSlot;
  banners: Banner[];
  onReload: () => void;
  onLocalChange: (updated: Banner) => void;
}

function SlotSection({ slot, banners, onReload, onLocalChange }: SectionProps) {
  const config = SLOT_CONFIG[slot];
  const [creating, setCreating] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [ordered, setOrdered] = useState(banners);

  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  useEffect(() => { setOrdered(banners); }, [banners]);

  const atMax = ordered.length >= config.max;

  const create = async () => {
    setCreating(true);
    try {
      await bannerService.create({
        slot,
        // Un placeholder, non un valore definitivo: `alt` è obbligatorio
        // lato backend e la card lo mostra subito in cima da compilare.
        alt: `Nuovo banner ${config.label.toLowerCase()}`,
        active: false,
      });
      toast.success('Banner creato — carica l\'immagine e compila il testo alternativo');
      onReload();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? 'Errore creazione banner');
    } finally {
      setCreating(false);
    }
  };

  const handleDragEnd = async () => {
    const from = dragItem.current;
    const to = dragOverItem.current;
    dragItem.current = null;
    dragOverItem.current = null;
    if (from === null || to === null || from === to) return;

    const next = [...ordered];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setOrdered(next.map((b, i) => ({ ...b, position: i })));

    setSavingOrder(true);
    try {
      await bannerService.reorder(slot, next.map(b => b.id));
      toast.success('Ordine salvato');
    } catch {
      toast.error('Errore salvataggio ordine');
      onReload();
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <section className="mb-8" data-testid={`banner-slot-${slot}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2">
            Slot {config.label}
            <span className="text-xs font-normal text-muted-foreground">
              {ordered.length} di {config.max} max
            </span>
            {savingOrder && (
              <span className="inline-flex items-center gap-1 text-xs font-normal text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" /> salvataggio…
              </span>
            )}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5 max-w-2xl">{config.description}</p>
        </div>
        <button
          onClick={create}
          disabled={creating || atMax}
          title={atMax
            ? `Massimo ${config.max} banner in questo slot: oltre non vengono visti. Elimina o disattiva un banner esistente.`
            : undefined}
          className="h-9 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium flex items-center gap-1.5 hover:bg-[hsl(214,58%,35%)] transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid={`banner-add-${slot}`}
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Nuovo banner
        </button>
      </div>

      {ordered.length === 0 ? (
        <div className="rounded-xl border bg-white p-10 text-center" data-testid={`banner-empty-${slot}`}>
          <ImageIcon className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="font-medium text-muted-foreground text-sm">Nessun banner in questo slot</p>
          <p className="text-xs text-muted-foreground/70 mt-1 max-w-sm mx-auto">
            {slot === 'hero'
              ? 'Senza banner attivi il widget "Ricerca avanzata" occupa tutta la larghezza dell\'hero.'
              : 'Senza banner attivi la vetrina prodotti occupa tutta la larghezza.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {ordered.map((b, index) => (
            <BannerCard
              key={b.id}
              banner={b}
              config={config}
              onChanged={onLocalChange}
              onDeleted={onReload}
              dragHandlers={{
                onDragStart: () => { dragItem.current = index; },
                onDragEnter: () => { dragOverItem.current = index; },
                onDragEnd: handleDragEnd,
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// ── Pagina ────────────────────────────────────────────────────────────────

export default function AdminBannerPage() {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setBanners(await bannerService.adminList());
    } catch {
      toast.error('Errore caricamento banner');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Aggiorna in locale il banner toccato invece di ricaricare tutto: un
  // reload completo perderebbe lo stato di modifica delle altre card.
  const onLocalChange = useCallback((updated: Banner) => {
    setBanners(prev => prev.map(b => (b.id === updated.id ? updated : b)));
  }, []);

  const bySlot = useMemo(() => {
    const map: Record<BannerSlot, Banner[]> = { hero: [], sidebar: [] };
    for (const b of banners) {
      if (map[b.slot]) map[b.slot].push(b);
    }
    for (const slot of SLOTS) {
      map[slot].sort((a, b) => a.position - b.position);
    }
    return map;
  }, [banners]);

  return (
    <div className="p-6" data-testid="admin-banner-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Banner homepage</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Le immagini della homepage. Trascina per riordinare dentro uno slot.
          Un banner senza immagine desktop non viene pubblicato; per togliere
          un banner dalla homepage senza perderne le impostazioni, togli la
          spunta ad "Attivo".
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…
        </div>
      ) : (
        SLOTS.map(slot => (
          <SlotSection
            key={slot}
            slot={slot}
            banners={bySlot[slot]}
            onReload={load}
            onLocalChange={onLocalChange}
          />
        ))
      )}
    </div>
  );
}
